import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plus, Star, ArrowLeft, Edit, Mail, Phone, MapPin, Calendar, Clock, DollarSign, Receipt, X, Home, ExternalLink, User, FileText } from 'lucide-react';
import { useCustomers } from '../hooks/useCustomers';
import { useJobs } from '../../jobs/hooks/useJobs';
import { useProperties } from '../../properties/hooks/useProperties';
import { useInvoices } from '../../invoices/hooks/useInvoices';
import { useRequests } from '../../requests/hooks/useRequests';
import { useQuotes } from '../../quotes/hooks/useQuotes';
import { DateUtils } from '@cleanpro/common-ui';

export default function CustomerDetail() {
  const { customerId } = useParams<{ customerId: string }>();
  const { customers, updateCustomer } = useCustomers();
  const { jobs, updateJobStatus } = useJobs();
  const { properties, addProperty, updateProperty } = useProperties();
  const { invoices } = useInvoices();
  const { requests } = useRequests();
  const { quotes } = useQuotes();
  const [jobFilter, setJobFilter] = useState<'all' | 'upcoming' | 'completed' | 'cancelled'>('all');
  const [isEditing, setIsEditing] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [propertyModalType, setPropertyModalType] = useState<'add' | 'associate'>('add');
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    preferences: ''
  });
  const [newProperty, setNewProperty] = useState({
    propertyType: 'house' as 'house' | 'apartment' | 'condo' | 'townhouse' | 'office',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    squareFootage: undefined as number | undefined,
    bedrooms: 0,
    bathrooms: 0,
    floors: 1,
    accessInstructions: '',
    specialInstructions: '',
    hasPets: false,
    petDetails: '',
    preferredCleaningDay: undefined as undefined | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
    requestedTimeSlot: undefined as undefined | 'morning' | 'midday' | 'afternoon',
    isActive: true
  });

  const customer = customers.find(c => c.id === customerId);
  const customerJobs = jobs.filter(b => b.customer?.id === customerId);
  const customerProperties = properties.filter(p => p.customerId === customerId);
  const customerInvoices = invoices.filter(i => i.customerId === customerId);
  const customerRequests = requests.filter(r => r.customer_id === customerId);
  const customerQuotes = quotes.filter(q => q.customerId === customerId);
  
  // Get unassigned properties (not associated with any customer)
  const unassignedProperties = properties.filter(p => !p.customerId || p.customerId === '');


  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer?.id) return;

    try {
      await updateCustomer({
        id: customer.id,
        ...editForm
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update customer:', error);
      // You might want to add error handling UI here
    }
  };

  const handleEditCancel = () => {
    if (customer) {
      setEditForm({
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
        preferences: customer.preferences
      });
    }
    setIsEditing(false);
  };

  const handleAddProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer) return;

    try {
      await addProperty({
        name: newProperty.addressLine1, // Use address as property name
        ...newProperty,
        customerId: customer.id
      });
      
      // Reset form
      setNewProperty({
        propertyType: 'house',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        squareFootage: undefined,
        bedrooms: 0,
        bathrooms: 0,
        floors: 1,
        accessInstructions: '',
        specialInstructions: '',
        hasPets: false,
        petDetails: '',
        preferredCleaningDay: undefined,
        requestedTimeSlot: undefined,
        isActive: true
      });
      
      setShowPropertyModal(false);
    } catch (error) {
      console.error('Failed to add property:', error);
    }
  };

  const handleAssociateProperty = async (propertyId: string) => {
    if (!customer) return;

    try {
      await updateProperty(propertyId, { customerId: customer.id });
      setShowPropertyModal(false);
    } catch (error) {
      console.error('Failed to associate property:', error);
    }
  };

  const handleDisassociateProperty = async (propertyId: string) => {
    try {
      await updateProperty(propertyId, { customerId: '' });
    } catch (error) {
      console.error('Failed to disassociate property:', error);
    }
  };

  const openPropertyModal = (type: 'add' | 'associate') => {
    setPropertyModalType(type);
    setShowPropertyModal(true);
  };

  const filteredJobs = customerJobs.filter(job => {
    if (jobFilter === 'all') return true;
    if (jobFilter === 'upcoming') return job.status === 'confirmed' || job.status === 'in-progress';
    if (jobFilter === 'completed') return job.status === 'completed';
    if (jobFilter === 'cancelled') return job.status === 'cancelled';
    return true;
  });

  const stats = {
    totalJobs: customerJobs.length,
    completedJobs: customerJobs.filter(b => b.status === 'completed').length,
    upcomingJobs: customerJobs.filter(b => b.status === 'confirmed' || b.status === 'in-progress').length,
    totalSpent: customerJobs.filter(b => b.status === 'completed').reduce((sum, b) => sum + b.price, 0),
    totalInvoices: customerInvoices.length,
    paidInvoices: customerInvoices.filter(i => i.status === 'paid').length,
    totalInvoiceAmount: customerInvoices.reduce((sum, i) => sum + (i.amount || 0), 0)
  };

  if (!customer) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/customers" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Customer Not Found</h1>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <p className="text-gray-500">The customer you're looking for doesn't exist.</p>
          <Link to="/customers" className="text-indigo-600 hover:text-blue-700 font-medium mt-2 inline-block">
            Return to Customers
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-yellow-100 text-yellow-800';
      case 'requested':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/customers" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{customer.name}</h1>
            <p className="text-gray-600">Customer Details & Job History</p>
          </div>
        </div>
        <button 
          onClick={() => setIsEditing(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Edit className="h-4 w-4" />
          <span>Edit Customer</span>
        </button>
      </div>

      {/* Customer Profile & Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Profile */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            {isEditing ? (
              /* Edit Form */
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Customer Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferences</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={editForm.preferences}
                    onChange={(e) => setEditForm({ ...editForm, preferences: e.target.value })}
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={handleEditCancel}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              /* Display Mode */
              <>
                <div className="text-center mb-6">
                  <div className="h-20 w-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-700">
                      {customer.name.charAt(0)}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">{customer.name}</h2>
                  <div className="flex items-center justify-center mt-2">
                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                    <span className="text-sm text-gray-600 ml-1">{customer.rating} rating</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{customer.email}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900">{customer.phone}</span>
                  </div>
                  <div className="flex items-start space-x-3 text-sm">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-900">{customer.address}</span>
                  </div>
                </div>

                {customer.preferences && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Preferences</h3>
                    <p className="text-sm text-gray-600">{customer.preferences}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Jobs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
                </div>
                <Calendar className="h-8 w-8 text-indigo-600" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.completedJobs}</p>
                </div>
                <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Upcoming</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.upcomingJobs}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Spent</p>
                  <p className="text-2xl font-bold text-gray-900">${stats.totalSpent}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Invoices</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalInvoices}</p>
                </div>
                <Receipt className="h-8 w-8 text-purple-600" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Invoice Total</p>
                  <p className="text-2xl font-bold text-gray-900">${stats.totalInvoiceAmount}</p>
                </div>
                <DollarSign className="h-8 w-8 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Properties */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Properties</h2>
            <div className="flex space-x-2">
              {unassignedProperties.length > 0 && (
                <button
                  onClick={() => openPropertyModal('associate')}
                  className="bg-indigo-600 text-white px-3 py-1 rounded-lg hover:bg-indigo-700 transition-colors text-sm flex items-center space-x-1"
                >
                  <Home className="h-4 w-4" />
                  <span>Associate Property</span>
                </button>
              )}
              <button
                onClick={() => openPropertyModal('add')}
                className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center space-x-1"
              >
                <Plus className="h-4 w-4" />
                <span>Add Property</span>
              </button>
            </div>
          </div>
        </div>
        <div className="p-6">
          {customerProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customerProperties.map((property) => (
                <div
                  key={property.id}
                  className="relative p-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all group"
                >
                  {/* Remove button */}
                  <button
                    onClick={() => handleDisassociateProperty(property.id)}
                    className="absolute top-2 right-2 p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors opacity-0 group-hover:opacity-100"
                    title="Remove property from customer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  
                  {/* Property content */}
                  <Link
                    to={`/properties/${property.id}`}
                    className="block"
                  >
                    <div className="flex items-start justify-between mb-3 pr-8">
                      <div className="flex items-center gap-2">
                        <Home className="h-5 w-5 text-indigo-600" />
                        <h3 className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                          {property.addressLine1}
                        </h3>
                      </div>
                      <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                    </div>
                    <p className="text-sm text-gray-600 capitalize mb-2">{property.propertyType}</p>
                    <div className="flex items-start gap-2 text-sm text-gray-500">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">
                        {property.addressLine1}{property.addressLine2 ? `, ${property.addressLine2}` : ''}, {property.city}, {property.state} {property.postalCode}
                      </span>
                    </div>
                    {(property.bedrooms || property.bathrooms || property.squareFootage) && (
                      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                        {property.bedrooms && property.bedrooms > 0 && (
                          <span>{property.bedrooms} bed</span>
                        )}
                        {property.bathrooms && property.bathrooms > 0 && (
                          <span>{property.bathrooms} bath</span>
                        )}
                        {property.squareFootage && (
                          <span>{property.squareFootage.toLocaleString()} sq ft</span>
                        )}
                      </div>
                    )}
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Home className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No properties assigned</h3>
              <p className="text-gray-500 mb-4">
                This customer doesn't have any properties associated with their account yet.
              </p>
              <div className="flex justify-center space-x-3">
                {unassignedProperties.length > 0 && (
                  <button
                    onClick={() => openPropertyModal('associate')}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                  >
                    <Home className="h-4 w-4" />
                    <span>Associate Existing Property</span>
                  </button>
                )}
                <button
                  onClick={() => openPropertyModal('add')}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add New Property</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Customer Requests */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Service Requests</h2>
        </div>
        <div className="p-6">
          {customerRequests.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customerRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/requests/${request.id}`} className="block hover:text-indigo-600 transition-colors">
                          <div className="text-sm font-medium text-gray-900">{request.services?.name || 'Service'}</div>
                          <div className="text-sm text-gray-500">{request.services?.description || request.notes}</div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/requests/${request.id}`} className="block hover:text-indigo-600 transition-colors">
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700 max-w-48 truncate">
                              {request.address}
                            </span>
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/requests/${request.id}`} className="block hover:text-indigo-600 transition-colors">
                          <div className="flex items-center text-sm text-gray-900">
                            <Calendar className="h-4 w-4 mr-2" />
                            {request.requested_date ? DateUtils.formatDateLong(request.requested_date) : 'Flexible'}
                          </div>
                          {request.requested_time && (
                            <div className="flex items-center text-sm text-gray-500">
                              <Clock className="h-4 w-4 mr-2" />
                              {request.requested_time}
                            </div>
                          )}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/requests/${request.id}`} className="block hover:text-indigo-600 transition-colors">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(request.status)}`}>
                            {request.status.replace('-', ' ')}
                          </span>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/requests/${request.id}`} className="block hover:text-indigo-600 transition-colors">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${
                            request.urgency === 'emergency' ? 'bg-red-100 text-red-800' :
                            request.urgency === 'normal' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {request.urgency}
                          </span>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/requests/${request.id}`}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No service requests</h3>
              <p className="text-gray-500">
                This customer hasn't submitted any service requests yet.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Customer Quotes */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Quotes</h2>
        </div>
        <div className="p-6">
          {customerQuotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quote #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Services
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valid Until
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customerQuotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/quotes/${quote.id}`} className="block hover:text-indigo-600 transition-colors">
                          <div className="text-sm font-medium text-gray-900">{quote.quoteNumber}</div>
                          {quote.requestId && (
                            <div className="text-sm text-gray-500">From Request</div>
                          )}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <Link to={`/quotes/${quote.id}`} className="block hover:text-indigo-600 transition-colors">
                          <div className="text-sm text-gray-900">
                            {quote.lineItems && Array.isArray(quote.lineItems) && quote.lineItems.length > 0 ? (
                              <div className="space-y-1">
                                {quote.lineItems.slice(0, 2).map((item, index) => (
                                  <div key={index} className="flex justify-between">
                                    <span className="truncate max-w-48">{item.description}</span>
                                    {quote.lineItems.length === 1 && (
                                      <span className="text-gray-500 ml-2">x{item.quantity}</span>
                                    )}
                                  </div>
                                ))}
                                {quote.lineItems.length > 2 && (
                                  <div className="text-gray-500 text-xs">
                                    +{quote.lineItems.length - 2} more item{quote.lineItems.length > 3 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500">No services listed</span>
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/quotes/${quote.id}`} className="block hover:text-indigo-600 transition-colors">
                          <div className="flex items-center text-sm font-medium text-gray-900">
                            <DollarSign className="h-4 w-4 mr-1" />
                            {quote.totalAmount?.toLocaleString() || '0'}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/quotes/${quote.id}`} className="block hover:text-indigo-600 transition-colors">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(quote.status)}`}>
                            {quote.status.replace('-', ' ')}
                          </span>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/quotes/${quote.id}`} className="block hover:text-indigo-600 transition-colors">
                          <div className="text-sm text-gray-900">
                            {quote.validUntil ? (
                              <div className={`${new Date(quote.validUntil) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>
                                {DateUtils.formatDateLong(quote.validUntil)}
                                {new Date(quote.validUntil) < new Date() && (
                                  <div className="text-xs text-red-500">Expired</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500">No expiry</span>
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/quotes/${quote.id}`} className="block hover:text-indigo-600 transition-colors">
                          <div className="text-sm text-gray-900">
                            {DateUtils.formatDateLong(quote.createdAt)}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/quotes/${quote.id}`}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No quotes</h3>
              <p className="text-gray-500">
                This customer doesn't have any quotes yet.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Customer Invoices */}
      {customerInvoices.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Invoices</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {customerInvoices.map((invoice) => (
                <Link
                  key={invoice.id}
                  to={`/invoices/${invoice.id}`}
                  className="block p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <Receipt className="h-5 w-5 text-purple-600" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {invoice.invoiceNumber || `Invoice #${invoice.id.slice(-6)}`}
                          </p>
                          <p className="text-sm text-gray-500">
                            Issued: {DateUtils.formatDateLong(invoice.issueDate)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">${invoice.amount}</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                          invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                          invoice.status === 'finalized' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </div>
                      <ExternalLink className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Jobs Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Job History</h2>
            <div className="flex space-x-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'upcoming', label: 'Upcoming' },
                { key: 'completed', label: 'Completed' },
                { key: 'cancelled', label: 'Cancelled' }
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setJobFilter(filter.key as 'all' | 'upcoming' | 'completed' | 'cancelled')}
                  className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                    jobFilter === filter.key
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {filteredJobs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/jobs/${job.id}`} className="block hover:text-indigo-600 transition-colors">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{job.service?.name || 'Service'}</p>
                          <p className="text-sm text-gray-500">{job.duration}h duration</p>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/jobs/${job.id}`} className="block hover:text-indigo-600 transition-colors">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700 line-clamp-2 max-w-48">
                            {job.property?.addressLine1 || 'No address'}
                          </span>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/jobs/${job.id}`} className="block hover:text-indigo-600 transition-colors">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">{job.business?.businessName || 'Unassigned'}</span>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/jobs/${job.id}`} className="block hover:text-indigo-600 transition-colors">
                        <div>
                          <div className="flex items-center text-sm text-gray-900">
                            <Calendar className="h-4 w-4 mr-2" />
                            {DateUtils.formatDateLong(job.date_time)}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="h-4 w-4 mr-2" />
                            {DateUtils.formatTimeFromDate(job.date_time)}
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/jobs/${job.id}`} className="block hover:text-indigo-600 transition-colors">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(job.status)}`}>
                          {job.status.replace('-', ' ')}
                        </span>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/jobs/${job.id}`} className="block hover:text-indigo-600 transition-colors">
                        <div className="flex items-center text-sm font-medium text-gray-900">
                          <DollarSign className="h-4 w-4 mr-1" />
                          {job.price}
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                        value={job.status}
                        onChange={(e) => updateJobStatus(job.id, e.target.value as 'requested' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled')}
                      >
                        <option value="requested">Requested</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No jobs found for the selected filter.</p>
          </div>
        )}
      </div>

      {/* Property Management Modal */}
      {showPropertyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {propertyModalType === 'add' ? 'Add New Property' : 'Associate Existing Property'}
              </h2>
              <button
                onClick={() => setShowPropertyModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {propertyModalType === 'associate' ? (
              // Associate existing property
              <div className="space-y-4">
                <p className="text-gray-600">
                  Select an unassigned property to associate with {customer?.name}:
                </p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {unassignedProperties.map((property) => (
                    <div
                      key={property.id}
                      className="p-4 border border-gray-200 rounded-lg hover:border-indigo-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Home className="h-4 w-4 text-indigo-600" />
                            <h3 className="font-medium text-gray-900">{property.addressLine1}</h3>
                          </div>
                          <p className="text-sm text-gray-600 capitalize mb-1">{property.propertyType}</p>
                          <p className="text-sm text-gray-500">
                            {property.addressLine1}{property.addressLine2 ? `, ${property.addressLine2}` : ''}, {property.city}, {property.state} {property.postalCode}
                          </p>
                        </div>
                        <button
                          onClick={() => handleAssociateProperty(property.id)}
                          className="bg-indigo-600 text-white px-3 py-1 rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                        >
                          Associate
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {unassignedProperties.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No unassigned properties available.</p>
                  </div>
                )}
              </div>
            ) : (
              // Add new property form
              <form onSubmit={handleAddProperty} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Property Type</label>
                  <select
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newProperty.propertyType}
                    onChange={(e) => setNewProperty({...newProperty, propertyType: e.target.value as 'house' | 'apartment' | 'condo' | 'townhouse' | 'office'})}
                  >
                    <option value="house">House</option>
                    <option value="apartment">Apartment</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="office">Office</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newProperty.addressLine1}
                    onChange={(e) => setNewProperty({...newProperty, addressLine1: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Address Line 2 (Optional)</label>
                  <input
                    type="text"
                    placeholder="Apt, Suite, etc."
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newProperty.addressLine2}
                    onChange={(e) => setNewProperty({...newProperty, addressLine2: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      value={newProperty.city}
                      onChange={(e) => setNewProperty({...newProperty, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      value={newProperty.state}
                      onChange={(e) => setNewProperty({...newProperty, state: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                      type="text"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      value={newProperty.postalCode}
                      onChange={(e) => setNewProperty({...newProperty, postalCode: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bedrooms</label>
                    <input
                      type="number"
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      value={newProperty.bedrooms}
                      onChange={(e) => setNewProperty({...newProperty, bedrooms: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bathrooms</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      value={newProperty.bathrooms}
                      onChange={(e) => setNewProperty({...newProperty, bathrooms: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Square Footage</label>
                    <input
                      type="number"
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      value={newProperty.squareFootage || ''}
                      onChange={(e) => setNewProperty({...newProperty, squareFootage: parseInt(e.target.value) || undefined})}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPropertyModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Property
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}